import React, { useState, useEffect, ChangeEvent } from 'react';
import { Table, Button, Input, Form, message, Space, Row, Col, Modal } from 'antd';
import apiClient from '../services/api';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import SupplierForm from '../components/SupplierForm';
import { fetchWithLoading } from '../utils/fetchHelper';
import { confirmDelete } from '../utils/confirmDelete';
import { accentInsensitiveIncludes } from '../utils/accentUtils';

interface Supplier {
  id: number;
  name: string;
  tax_code?: string;
  contact_phone?: string;
}

const SupplierPage = () => {
  const [form] = Form.useForm();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false);

  const fetchSuppliers = async () => {
    await fetchWithLoading(
      () => apiClient.getSuppliers(),
      setLoading,
      (data) => setSuppliers(data as Supplier[]),
      'Không thể tải danh sách nhà cung cấp'
    );
  };

  useEffect(() => { fetchSuppliers(); }, []);

  const handleSubmit = async (values: any) => {
    try {
      const submitData = {
        name: values.name,
        tax_code: values.tax_code,
        contact_phone: values.contact_phone,
      };
      if (editingSupplier) {
        await apiClient.updateSupplier(editingSupplier.id, submitData);
        message.success('Cập nhật nhà cung cấp thành công!');
      } else {
        await apiClient.createSupplier(submitData);
        message.success('Tạo nhà cung cấp thành công!');
      }
      form.resetFields();
      setEditingSupplier(null);
      setModalVisible(false);
      fetchSuppliers();
    } catch (err: any) {
      const msg = err?.response?.data?.message || (editingSupplier ? 'Cập nhật thất bại!' : 'Tạo nhà cung cấp thất bại!');
      message.error(msg);
    }
  };

  const handleEdit = (record: Supplier) => {
    setEditingSupplier(record);
    form.setFieldsValue({ name: record.name, tax_code: record.tax_code, contact_phone: record.contact_phone });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteSupplier(id);
      message.success('Đã xóa nhà cung cấp');
      fetchSuppliers();
    } catch {
      message.error('Xóa nhà cung cấp thất bại!');
    }
  };

  const handleDeleteClick = (record: Supplier) => {
    confirmDelete('Bạn có chắc muốn xóa nhà cung cấp này?', () => handleDelete(record.id));
  };

  const filteredSuppliers = suppliers.filter((s) =>
    accentInsensitiveIncludes(s.name, searchValue) ||
    accentInsensitiveIncludes(s.tax_code || '', searchValue)
  );
  const columns = [
    { title: 'Tên nhà cung cấp', dataIndex: 'name', key: 'name' },
    { title: 'Mã số thuế', dataIndex: 'tax_code', key: 'tax_code' },
    { title: 'Số điện thoại', dataIndex: 'contact_phone', key: 'contact_phone' },
    {
      title: 'Hành động', key: 'action', width: 110,
      render: (_: any, record: Supplier) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteClick(record)} />
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col xs={24} md={20}>
          <Input
            className="ams-input"
            placeholder="Tìm kiếm nhà cung cấp"
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            allowClear
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button className="ams-btn" type="primary" size="large" icon={<EditOutlined />} style={{ borderRadius: 8, height: 40, width: '100%' }} onClick={() => { setEditingSupplier(null); form.resetFields(); setModalVisible(true); }}>Thêm mới</Button>
        </Col>
      </Row>
      <Modal
        open={modalVisible}
        title={editingSupplier ? 'Cập nhật nhà cung cấp' : 'Tạo mới nhà cung cấp'}
        onCancel={() => { setModalVisible(false); setEditingSupplier(null); form.resetFields(); }}
        footer={null}
      >
        <SupplierForm
          form={form}
          initialValues={editingSupplier || {}}
          onFinish={handleSubmit}
          loading={loading}
          submitText={editingSupplier ? 'Cập nhật' : 'Tạo mới'}
        />
        {editingSupplier && (
          <Button className="ams-btn" style={{ width: '100%', borderRadius: 8, height: 40, marginTop: 8 }} size="large" onClick={() => { setEditingSupplier(null); form.resetFields(); setModalVisible(false); }}>Hủy</Button>
        )}
      </Modal>
      <Table
        dataSource={filteredSuppliers}
        rowKey="id"
        loading={loading}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default SupplierPage; 